import type { PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"

import { AppContent } from "@/components/app-content"
import { AppShell } from "@/components/app-shell"
import { BrandSidebar } from "@/components/brand-sidebar"
import { BrandSwitcher } from "@/components/brand-switcher"
import { Breadcrumbs } from "@/components/breadcrumbs"
import { CurrentBrandDisplay } from "@/components/current-brand-display"
import { HeaderNavUser } from "@/components/header-nav-user"
import { BellTrigger } from "@/components/notifications/bell-trigger"
import { RoleBadge } from "@/components/role-badge"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { useCurrentContext } from "@/hooks/use-current-context"
import { usePollUnreadCount } from "@/hooks/use-poll-unread-count"
import {
  FIXED_VIEWPORT_CHAIN,
  useIsFixedViewportSurface,
} from "@/layouts/app/fixed-viewport-surface"
import { cn } from "@/lib/utils"
import type { BreadcrumbItem } from "@/types"

interface BrandShellProps {
  breadcrumbs?: BreadcrumbItem[]
}

/**
 * BrandShell — shell for the `brand` role (and for an OKS Admin acting-as a
 * brand, see OKS-346 below).
 *
 * ⚠️ NOT the agency shell. It served both roles until the OKS-13 F1 split gave
 * `agency` its own `AgencyShell`; this docstring went on claiming otherwise
 * until OKS-1041, where that stale sentence is the likeliest reason nobody
 * checked whether OKS-820's fixed-viewport fix had ever reached the agency
 * console. It had not, for three tickets. A docstring that overstates a
 * component's reach suppresses exactly the question that would find the gap.
 *
 * Header composition (CTPO-locked, ≥1024px):
 *   SidebarTrigger · Breadcrumbs · CurrentBrandBadge · spacer ·
 *   BrandSwitcher · RoleBadge · NavUser
 *
 * Responsive: 768-1023 hides Breadcrumbs. <768 hides Breadcrumbs + RoleBadge,
 * truncates brand name to 14ch. BrandSwitcher always visible (in agency multi-
 * brand case) — on the smallest screens it remains in the header but the trigger
 * shrinks. (Future: relocate switcher into NavUser dropdown on <768 — not
 * required for OKS-5 MVP.)
 *
 * OKS-346 — When an OKS Admin is acting-as a brand (`actingAs != null`),
 * the `<BrandSwitcher>` is hidden (UX Spec OQ2). Admins switch brands by
 * exiting + re-entering act-as from the Brands console, not inline.
 * BrandSwitcher would also self-hide on `availableBrands.length ≤ 1`
 * (the typical case for an admin, who has no agency assignments), but the
 * explicit gate makes intent legible and decouples this contract from
 * `availableBrands` semantics evolving.
 */
export function BrandShell({
  children,
  breadcrumbs = [],
}: PropsWithChildren<BrandShellProps>) {
  const { currentUser, actingAs } = useCurrentContext()
  const { t } = useTranslation()
  const role = currentUser?.role
  // OKS-36 — bell badge polling. Mounted once at the shell level so a
  // single 30s tick covers the whole authed-brand surface.
  usePollUnreadCount()

  // OKS-820 — the brand Messages surface is a FIXED-VIEWPORT chat: its thread
  // history scrolls INTERNALLY while the header + composer stay put, so the
  // whole console chain must be bounded to the viewport rather than body-scroll.
  // Every other brand console page is a normal body-scroll document. We branch
  // on the active Inertia page component (stable, path-based) so ONLY Messages
  // opts into the bound; admin/agency/other brand pages are byte-identical.
  //
  // The bound is applied along the sidebar/inset chain (distinct from the
  // creator flex-column shell): `app-layout` wraps this in `h-[100dvh]
  // overflow-hidden`, then here the sidebar wrapper (`AppShell` passthrough),
  // the `SidebarInset` (`AppContent` className), and the content region below
  // each get `min-h-0 flex-1` so height flows down and the page's own
  // `flex min-h-0 flex-1` history band is the single internal scroller — no
  // fixed-height guess anywhere (that guess was the OKS-820 root cause).
  //
  // OKS-1041 — the predicate and the three class strings moved to
  // `fixed-viewport-surface.ts` so `AgencyShell` applies the SAME chain from
  // the SAME source. The classes applied here are unchanged; this is a pure
  // extraction on the brand side, and the OKS-820 spec block below is the
  // regression control that says so.
  const isFixedViewportSurface = useIsFixedViewportSurface()

  return (
    <AppShell
      variant="sidebar"
      className={cn(isFixedViewportSurface && FIXED_VIEWPORT_CHAIN.shell)}
    >
      <BrandSidebar />
      <AppContent
        variant="sidebar"
        className={cn(
          "overflow-x-hidden",
          isFixedViewportSurface && FIXED_VIEWPORT_CHAIN.content,
        )}
      >
        <header
          className="border-sidebar-border/50 flex h-16 shrink-0 items-center gap-3 border-b px-4 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 md:px-6"
          data-testid="brand-shell-header"
        >
          <SidebarTrigger className="-ml-1" />
          <div className="hidden lg:flex">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>
          <div
            className="bg-border mx-1 hidden h-5 w-px lg:block"
            aria-hidden="true"
          />
          <CurrentBrandDisplay truncateAt={20} className="hidden sm:flex" />
          <CurrentBrandDisplay truncateAt={14} className="flex sm:hidden" />
          <div className="flex-1" />
          {!actingAs && <BrandSwitcher />}
          {role && <RoleBadge role={role} className="hidden md:inline-flex" />}
          <BellTrigger />
          <HeaderNavUser />
        </header>
        {/*
          `SidebarInset` already renders a `<main>` landmark — wrapping again
          here created nested `<main>` (A11y violation). Keep a focus-target
          region so focus returns cleanly after a brand switch reload, but
          don't emit a second main landmark.
         */}
        <div
          tabIndex={-1}
          role="region"
          aria-label={t("shell.content_region_label")}
          // OKS-820 — on the Messages route this region becomes a bounded flex
          // column (`flex min-h-0 flex-1 flex-col`) so the page's own flex chain
          // can size the thread history to the remaining space and scroll it
          // internally. Every other brand page keeps the plain growing `flex-1`
          // = same body-scroll as before.
          className={cn(
            isFixedViewportSurface ? FIXED_VIEWPORT_CHAIN.region : "flex-1",
          )}
        >
          {children}
        </div>
      </AppContent>
    </AppShell>
  )
}
